import { StrictMode, Suspense, Fragment } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import '../app.css';
import './lib/i18n';
import App from './App.tsx';
import { ErrorFallback } from './components/ErrorFallback';
import { APP_VERSION } from './config/version';
import { safeStorage } from './lib/safe-storage';
import { Toaster } from './components/ui/sonner';

// Initialize Sentry BEFORE rendering React
if (import.meta.env.VITE_GLITCHTIP_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_GLITCHTIP_DSN,
    environment: import.meta.env.VITE_GLITCHTIP_ENVIRONMENT || 'development',

    // Performance Monitoring
    integrations: [
      // Browser tracing for performance (auto-detects React Router)
      Sentry.browserTracingIntegration(),
      // Session replay for debugging
      Sentry.replayIntegration({
        maskAllText: true, // Privacy: hide all text
        blockAllMedia: true, // Privacy: hide all images/videos
      }),
    ],

    // Performance traces sample rate
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0, // 10% in prod, 100% in dev

    // Session replay sample rates
    replaysSessionSampleRate: 0.1, // 10% of normal sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

    // Release tracking (from package.json version)
    release: APP_VERSION,

    // Ignore common non-critical errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      // Random plugins/extensions
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      // Chrome extensions
      "Can't find variable: ZiteReader",
      'jigsaw is not defined',
      'ComboSearch is not defined',
      // Facebook borked
      'fb_xd_fragment',
      // ISP injected scripts
      '__gCrWeb',
      '__pad',
      // Common React errors that aren't actionable
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
      // Network errors
      'NetworkError',
      'ChunkLoadError',
      'Failed to fetch',
      // MIME type errors (handled by auto-reload)
      'is not a valid JavaScript MIME type',
      'Unexpected Application Error',
    ],

    // Don't send certain URLs
    denyUrls: [
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^moz-extension:\/\//i,
    ],

    // Privacy: scrub sensitive data before sending
    beforeSend(event, _hint) {
      // Check if user has consented to error reporting
      // Use safeStorage to handle unavailable storage (SSR, incognito, etc.)
      const hasConsent = safeStorage.getItem('analytics-consent');
      if (hasConsent === 'false') {
        return null; // Don't send if user opted out
      }
      // If storage unavailable (returns null), treat as no explicit opt-out
      // so errors are sent by default (fail-open for error reporting)

      // Remove cookies (privacy)
      if (event.request?.cookies) {
        delete event.request.cookies;
      }

      // Remove auth headers
      if (event.request?.headers) {
        delete event.request.headers.Authorization;
        delete event.request.headers.authorization;
      }

      // Scrub localStorage/sessionStorage
      if (event.contexts?.storage) {
        delete event.contexts.storage;
      }

      return event;
    },
  });
}

// Use StrictMode only in development to avoid double-rendering portal issues in production
const Wrapper = import.meta.env.DEV ? StrictMode : Fragment;

createRoot(document.getElementById('root')!).render(
  <Wrapper>
    {/* Toaster placed outside error boundaries to prevent portal lifecycle conflicts */}
    <Toaster position='top-center' />
    <Sentry.ErrorBoundary
      fallback={(props) => (
        <ErrorFallback
          error={props.error as Error}
          resetError={props.resetError}
        />
      )}
      showDialog
    >
      <Suspense fallback={<div>Loading...</div>}>
        <App />
      </Suspense>
    </Sentry.ErrorBoundary>
  </Wrapper>
);
